import {
  ArchiveBoxArrowDownIcon,
  ArrowDownTrayIcon,
  ArrowLeftCircleIcon,
  ArrowRightCircleIcon,
  ArrowUpTrayIcon,
  BarsArrowDownIcon,
  BarsArrowUpIcon,
  BookOpenIcon,
  CloudArrowUpIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { notifyDocumentsPanel } from 'components/AssistantModal/assistantPanelLayout'
import { SourceBox, TargetBox } from 'components/DragDrop'
import { LayoutLoading } from 'components/LayoutLoading'
import { DocumentsRefreshInterval } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { NewDocumentsTable } from 'pages/LoanSubmission/NewDocumentsTable'
import type { LoanSubmissionDocument } from 'pages/LoanSubmission/types'
import { MailDocumentModal } from 'pages/Servicing/PrintMail/MailDocumentModal'
import { usePrintMailOffer } from 'pages/Servicing/PrintMail/usePrintMailOffer'
import { useEffect, useMemo, useRef, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend, NativeTypes } from 'react-dnd-html5-backend'
import { useSelector } from 'react-redux'
import {
  downloadS3Document,
  getLoanSubmissionDocuments,
  getLoanSubmissionUpdatedAt,
  openS3Document,
  openS3Documents,
  updateLoanSubmission,
  uploadFiles,
} from 'services'
import Api from 'services/api'
import { Checkbox, DocumentFile, Input2, Tooltip } from 'stories/components'
import type { CategoryDocument } from 'stories/components/CategoryFileTable/CategoryFileTable'
import { formatTime } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

import { copyDroppedDocuments, isForeignDocumentDrop } from './droppedDocuments'
import { floaterPanelEdgeClass, floaterTabEdgeClass, FloaterTabWrapper, useFloaterTab } from './useFloaterTab'

const SIMPLIFIED_DOCUMENTS_PANEL_WIDTH = 480

export const DocumentsSlideNameType = ({
  loading: _loading,
  documents: _documents,
  defaultShow = false,
  position = 'left',
  // loadDocs = false,
  borrowerSide = false,
  onShow = () => {},
}: {
  loading: boolean
  documents?: CategoryDocument[]
  defaultShow?: boolean
  position?: 'left' | 'right'
  loadDocs?: boolean
  borrowerSide?: boolean
  onShow?: Function
}) => {
  const [documents, setDocuments] = useState<CategoryDocument[]>([])
  const [isShowDocuments, setShowDocuments] = useState(defaultShow)
  const floater = useFloaterTab(borrowerSide ? 'borrowerDocuments' : 'documentsByName', {
    side: position,
    percent: 25,
    mobileTop: 192,
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [sortKey, setSortKey] = useState('')
  const [sortDir, setSortDir] = useState(true)
  const [loading, setLoading] = useState(false)
  const [uploadingFiles, setUploadingFiles] = useState<DocumentFile[]>([])
  const lastUpdatedAtRef = useRef<number>(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { hasPermission } = usePermissions()
  const profile = useSelector((state: any) => state.auth.profile)
  setLoanNumber()

  // `hasPermission` answers undefined until the permission fetch lands. Coerced to false, so a
  // button that spends postage is never offered on a guess. Never on the borrower side: a borrower
  // has no business posting mail out of the company's account.
  const canSpendPostage = !borrowerSide && !!hasPermission('PrintMail.Send')
  const { canOffer: canMailDocuments } = usePrintMailOffer(isShowDocuments && canSpendPostage)
  const [mailingDocument, setMailingDocument] = useState<CategoryDocument | null>(null)

  const fetchDocuments = () => {
    setLoading(true)
    getLoanSubmissionDocuments()
      .then((res) => {
        if (res.success) {
          setDocuments(res.data)
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (isShowDocuments) {
      if (documents.length) return
      fetchDocuments()
    } else {
      if (_documents) setDocuments(_documents)
    }
  }, [_documents, isShowDocuments])

  useEffect(() => {
    if (!isShowDocuments) return

    let cancelled = false

    const pollUpdatedAt = () => {
      getLoanSubmissionUpdatedAt()
        .then((res) => {
          if (cancelled) return
          if (res && res !== lastUpdatedAtRef.current) {
            if (lastUpdatedAtRef.current) fetchDocuments()
            lastUpdatedAtRef.current = res
          }
        })
        .catch((error) => {
          if (cancelled) return
          console.error('Error fetching documents updated at:', error)
        })
    }

    pollUpdatedAt()
    const intervalId = setInterval(pollUpdatedAt, DocumentsRefreshInterval)

    return () => {
      cancelled = true
      clearInterval(intervalId)
    }
  }, [isShowDocuments])

  const sortedDocuments = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    let docs = documents
      .filter((doc: any) => {
        if (!query) return true
        return (
          (doc.name || '').toLowerCase().includes(query) ||
          (doc.status || '').toLowerCase().includes(query) ||
          (doc.category || '').toLowerCase().includes(query)
        )
      })
      .sort((a: any, b: any) => {
        const aV = a[sortKey]
        const bV = b[sortKey]
        if (aV > bV) return 1
        if (aV == bV) return 0
        return -1
      })
    if (sortDir) docs = docs.reverse()
    return docs
  }, [documents, sortDir, sortKey, searchQuery])

  const isSelectedBtnDisabled = useMemo(() => Object.keys(selected).length == 0, [selected])

  const onShowDocuments = () => {
    const nextState = !isShowDocuments
    setShowDocuments(nextState)
    notifyDocumentsPanel({ open: nextState, side: floater.side, width: SIMPLIFIED_DOCUMENTS_PANEL_WIDTH })
    onShow(nextState)
  }

  const onSelected = (key: string, value: boolean) => {
    const newData = cloneDeep(selected)
    if (!value) delete newData[key]
    else newData[key] = value
    setSelected(newData)
  }

  const onSelectAll = () => {
    const count = documents.length
    const selCount = Object.keys(selected).length
    let newData = cloneDeep(selected)
    if (count != selCount) documents.forEach((doc: CategoryDocument) => (newData[doc.fileKey] = true))
    else newData = {}
    setSelected(newData)
  }

  const onSort = (key: string) => {
    setSortKey(key)
    if (sortKey == key) setSortDir(!sortDir)
  }

  const onDownload = (format: 'zip' | 'pdf') => {
    const loanNumber = Api.getLoanNumber()

    const filename = `Documents-${loanNumber}`
    const fileKeys = Object.keys(selected).filter((fileKey) => documents.find((item) => item.fileKey === fileKey))
    openS3Documents(Number(loanNumber), filename, fileKeys, '', format)
  }

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target
    if (files?.length) onUploadFiles(Array.from(files))
    e.target.value = ''
  }

  const onUploadFiles = async (files: File[]) => {
    setLoading(true)
    const loanNumber = setLoanNumber()
    const uploadData = {
      path: `loan/${loanNumber}/document`,
    }
    let keys: string[] = []
    try {
      keys = await uploadFiles(uploadData, files)
    } catch {
      // All the files go up in one request, so a failure means none of them landed. Without this the
      // loading overlay never lifts and the panel can only be recovered by reloading the page.
      return
    } finally {
      setLoading(false)
    }
    const now = Date.now()

    const newFiles = cloneDeep(uploadingFiles)
    newFiles.push(
      ...files.map((file, index) => ({
        id: now + index,
        name: file.name,
        fileKey: keys[index],
        status: 'Not Reviewed',
        category: '',
        createdAt: now,
      })),
    )
    setUploadingFiles(newFiles)
  }

  /**
   * A drop on the storage is either OS files, which upload, or files the loan already holds
   * elsewhere (an exception's attachments), which are copied in. Both land in the same
   * categorize-and-save table as a fresh upload.
   */
  const onDropFiles = async (item: Record<string, any>) => {
    if (item?.files) return onUploadFiles(item.files as File[])
    const heldKeys = documents.map((doc) => doc.fileKey)
    setLoading(true)
    let copied: DocumentFile[] = []
    try {
      copied = await copyDroppedDocuments(item, setLoanNumber(), heldKeys)
    } catch {
      return
    } finally {
      setLoading(false)
    }
    if (copied.length) setUploadingFiles([...cloneDeep(uploadingFiles), ...copied])
  }

  const onAddDocuments = async (docs: DocumentFile[]) => {
    const newDocs = cloneDeep(documents)
    const addingDocs: LoanSubmissionDocument[] = docs.map((doc) => ({
      conditionNo: 0,
      name: doc.name || doc.fileKey?.split('/').pop() || 'document',
      key: doc.fileKey,
      status: doc.status || 'Not Reviewed',
      category: doc.category || '',
      createdAt: doc.createdAt || Date.now(),
      generated: false,
      createdBy: profile.email,
      borrower: borrowerSide,
    }))

    newDocs.push(...docs)
    setDocuments(newDocs)
    setLoading(true)
    await updateLoanSubmission({}, { update: addingDocs })
    setLoading(false)
  }

  const renderHeader = (key: string, title: any) => {
    return (
      <div className="flex flex-wrap font-normal items-center justify-center">
        <p>{title}</p>

        <span className="ml-2">
          {sortKey == key &&
            (sortDir ? <BarsArrowUpIcon className="w-3 h-3" /> : <BarsArrowDownIcon className="w-3 h-3" />)}
        </span>
      </div>
    )
  }

  const renderRow = (document: CategoryDocument, index: number) => {
    const getDragItem = () => {
      const selectedDocs = documents.filter((doc) => selected[doc.fileKey])
      if (selectedDocs.length > 1 && selected[document.fileKey]) {
        return {
          ...document,
          documents: selectedDocs,
        }
      }
      return document
    }
    return [
      <SourceBox item={getDragItem} type="document" key={document.fileKey}>
        <tr className={`border-b ${index % 2 ? 'bg-gray-50' : ''}`} key={`first-${document.fileKey}`}>
          <td className="px-3 py-2">
            <Checkbox
              id={`doc-no-${document.fileKey}`}
              title={`${document.name}`}
              checked={!!selected[document.fileKey]}
              onChange={(value) => onSelected(document.fileKey, value)}
            />
            <span className="italic text-[13px]">- {document.category || 'n/a'}</span>
            <span className="text-[13px] ml-2">({document.status || ''})</span>
          </td>
          <td className="px-2 py-1">
            <span className="italic text-[13.5px]">{formatTime(document.createdAt)}</span>
          </td>
          <td className="px-2 py-1 text-center">
            <button
              className="p-1 cursor-pointer hover-shadow1 rounded text-shade-blue"
              onClick={() => openS3Document(document.fileKey)}
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            <button
              className="p-1 cursor-pointer hover-shadow1 rounded"
              onClick={() => downloadS3Document(document.fileKey, document.name)}
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
            </button>
            {canMailDocuments && (
              <Tooltip message="Print and post this document" placement="right">
                <button
                  className="p-1 cursor-pointer hover-shadow1 rounded text-shade-blue"
                  onClick={() => setMailingDocument(document)}
                >
                  <PrinterIcon className="w-4 h-4" />
                </button>
              </Tooltip>
            )}
          </td>
        </tr>
      </SourceBox>,
    ]
  }

  const canUploadFile = hasPermission('Loan.Document.Upload') || borrowerSide

  return (
    <DndProvider backend={HTML5Backend}>
      <FloaterTabWrapper floater={floater}>
        <button
          className={`p-3 ${floaterTabEdgeClass(
            floater.side,
          )} ring-[1px] ring-shade-blue bg-shade-blue text-white border hover:bg-white border-shade-blue hover:text-shade-blue rounded flex items-center gap-2 w-auto sm:w-[155px]`}
          {...floater.tabProps(onShowDocuments)}
        >
          <BookOpenIcon className="w-6 h-6" />
          <span className="hidden sm:inline">Documents</span>
        </button>
      </FloaterTabWrapper>
      {isShowDocuments && (
        <div
          className={`shadow1 fixed bg-white border z-50 top-[5vh] bottom-0 w-[30rem] max-w-full h-[90vh] overflow-auto ease-in-out ${floaterPanelEdgeClass(
            floater.side,
          )}`}
        >
          <LayoutLoading show={loading || _loading} />
          <div className="w-[30rem] max-w-full fixed z-20 rounded-t flex justify-between p-3 font-bold text-sm place-items-center items-center text-shade-blue border-b bg-white gap-4">
            <p className="text-lg text-gray-800 flex-1">Documents</p>

            <div className="flex items-center">
              <p className="mr-1 font-bold">Selected</p>
              <button
                className={`${isSelectedBtnDisabled ? 'text-blue-400' : 'rounded hover-shadow1'}`}
                onClick={() => onDownload('zip')}
                disabled={isSelectedBtnDisabled}
              >
                <div className="flex p-1">
                  <Tooltip message="Download as Zip" position="bottom">
                    <ArchiveBoxArrowDownIcon className="w-5 h-5" />
                  </Tooltip>
                </div>
              </button>
              <button
                className={`${isSelectedBtnDisabled ? 'text-blue-400' : 'rounded hover-shadow1'} mr-4`}
                onClick={() => onDownload('pdf')}
                disabled={isSelectedBtnDisabled}
              >
                <div className="flex p-1">
                  <Tooltip message="Download as combined PDF" position="bottom">
                    <DocumentArrowDownIcon className="w-5 h-5" />
                  </Tooltip>
                </div>
              </button>
              <Checkbox
                title={`${Object.keys(selected).length}/${documents.length}`}
                id="documentSelected"
                checked={Object.keys(selected).length == documents.length}
                onClick={onSelectAll}
                disabled={documents.length == 0}
              />
            </div>

            {canUploadFile && (
              <>
                <input ref={fileInputRef} type="file" className="hidden" multiple onChange={onFileInputChange} />
                <Tooltip message="Upload Files">
                  <button
                    type="button"
                    className="hover:underline p-1 hover-shadow1 rounded"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <ArrowUpTrayIcon className="w-5 h-5" />
                  </button>
                </Tooltip>
              </>
            )}

            <button className="hover:underline p-1 hover-shadow1 rounded" onClick={onShowDocuments}>
              {floater.side === 'right' ? (
                <ArrowRightCircleIcon className="w-5 h-5" />
              ) : (
                <ArrowLeftCircleIcon className="w-5 h-5" />
              )}
            </button>
          </div>
          <div className="relative w-full pt-14 h-fit overflow-auto">
            <NewDocumentsTable
              files={uploadingFiles}
              setFiles={setUploadingFiles}
              onSubmit={async (files: DocumentFile[]) => {
                await onAddDocuments(files)
                setUploadingFiles([])
              }}
              onCancel={() => setUploadingFiles([])}
            />

            {!uploadingFiles.length && (
              <div className="p-1 relative">
                {canUploadFile && (
                  <div className="absolute top-0 right-[calc(50%-135px)] w-full h-fit">
                    <div className="flex flex-wrap items-center absolute top-0 right-0 border-dashed border-2 border-gray-300 px-4 py-8 text-gray-300 w-[270px] text-center">
                      <CloudArrowUpIcon className="w-10 h-10 mx-auto" />
                      <span>Drag and drop files.</span>
                    </div>
                  </div>
                )}
                <TargetBox
                  canDrop={(data: any) => {
                    if (!canUploadFile) return false
                    return (
                      !!data?.files ||
                      isForeignDocumentDrop(
                        data,
                        documents.map((doc) => doc.fileKey),
                      )
                    )
                  }}
                  types={['document', NativeTypes.FILE]}
                  onDrop={onDropFiles}
                >
                  <div className="w-full p-3">
                    <Input2
                      type="search"
                      title="Search"
                      hasIcon
                      transparent
                      icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
                      value={searchQuery}
                      onChange={(value) => setSearchQuery(value)}
                    />
                  </div>
                  <div className="relative overflow-y-auto h-[calc(90vh-150px)]">
                    <table className="w-full font-normal text-sm text-left text-black relative">
                      <thead className="border-b text-center bg-black/5">
                        <tr>
                          <th
                            className="px-2 py-1 border-r border-slate-300 cursor-pointer text-center"
                            onClick={() => onSort('name')}
                          >
                            {renderHeader('name', 'Name')}
                          </th>
                          <th
                            className="px-2 py-1 border-r border-slate-300 cursor-pointer w-32"
                            onClick={() => onSort('createdAt')}
                          >
                            {renderHeader('createdAt', 'Uploaded Date')}
                          </th>
                          <th className="px-2 py-1 font-normal">Action</th>
                        </tr>
                      </thead>

                      <tbody className="text-gray-900">
                        {sortedDocuments.map((document: any, index: number) => renderRow(document, index))}
                      </tbody>
                    </table>
                  </div>
                </TargetBox>
              </div>
            )}
          </div>
        </div>
      )}

      {!!mailingDocument && (
        <MailDocumentModal
          loanNumber={Number(Api.getLoanNumber())}
          documentKey={mailingDocument.fileKey}
          documentName={mailingDocument.name || mailingDocument.fileKey?.split('/').pop() || 'document.pdf'}
          onClose={() => setMailingDocument(null)}
        />
      )}
    </DndProvider>
  )
}
